/**
 * XML RTS Model 950 — Retenue d'impot sur les salaires
 * Format conforme XSD ACD (Administration des Contributions Directes)
 *
 * Genere le fichier XML mensuel de declaration des retenues d'impot.
 * Soumis via MyGuichet.lu ou plateforme ACD.
 */

export interface RTSEmployeeData {
  matriculeCcss: string
  lastName: string
  firstName: string
  birthDate: string
  taxClass: string
  grossSalaryCents: number
  taxableIncomeCents: number
  rtsCents: number
  creditCISCents: number
  creditCICCents: number
  creditCICO2Cents: number
}

export interface RTSDeclarationData {
  employerNumber: string
  employerName: string
  employerVatNumber: string
  periodYear: number
  periodMonth: number
  employees: RTSEmployeeData[]
}

/**
 * Genere le XML RTS Model 950 mensuel.
 */
export function generateRTSXML(data: RTSDeclarationData): string {
  const period = `${data.periodYear}-${String(data.periodMonth).padStart(2, '0')}`
  const totalRTS = data.employees.reduce((s, e) => s + e.rtsCents, 0)

  const employeeNodes = data.employees.map(emp => `
    <Salarie>
      <Matricule>${escapeXml(emp.matriculeCcss)}</Matricule>
      <Nom>${escapeXml(emp.lastName)}</Nom>
      <Prenom>${escapeXml(emp.firstName)}</Prenom>
      <DateNaissance>${emp.birthDate}</DateNaissance>
      <ClasseImpot>${emp.taxClass}</ClasseImpot>
      <SalaireBrut>${centsToDecimal(emp.grossSalaryCents)}</SalaireBrut>
      <RevenuImposable>${centsToDecimal(emp.taxableIncomeCents)}</RevenuImposable>
      <RetenueImpot>${centsToDecimal(emp.rtsCents)}</RetenueImpot>
      <CreditCIS>${centsToDecimal(emp.creditCISCents)}</CreditCIS>
      <CreditCIC>${centsToDecimal(emp.creditCICCents)}</CreditCIC>
      <CreditCICO2>${centsToDecimal(emp.creditCICO2Cents)}</CreditCICO2>
    </Salarie>`).join('')

  return `<?xml version="1.0" encoding="UTF-8"?>
<DeclarationRTS xmlns="urn:lu:acd:rts:model950" version="1.0">
  <Employeur>
    <NumeroEmployeur>${escapeXml(data.employerNumber)}</NumeroEmployeur>
    <Denomination>${escapeXml(data.employerName)}</Denomination>
    <NumeroTVA>${escapeXml(data.employerVatNumber)}</NumeroTVA>
  </Employeur>
  <Periode>${period}</Periode>
  <NombreSalaries>${data.employees.length}</NombreSalaries>
  <TotalRetenue>${centsToDecimal(totalRTS)}</TotalRetenue>
  <Salaries>${employeeNodes}
  </Salaries>
</DeclarationRTS>`
}

/**
 * XML ECSP — Etat Collectif des Salaires et Pensions (annuel)
 * Soumis annuellement a l'ACD avant le 1er mars.
 */
export interface ECSPEmployeeData {
  matriculeCcss: string
  lastName: string
  firstName: string
  birthDate: string
  taxClass: string
  entryDate: string
  exitDate: string | null
  annualGrossCents: number
  annualTaxableIncomeCents: number
  annualRTSCents: number
  annualCreditsCents: number
  monthsWorked: number
}

export interface ECSPData {
  employerNumber: string
  employerName: string
  employerVatNumber: string
  year: number
  employees: ECSPEmployeeData[]
}

export function generateECSPXML(data: ECSPData): string {
  const totalRTS = data.employees.reduce((s, e) => s + e.annualRTSCents, 0)
  const totalGross = data.employees.reduce((s, e) => s + e.annualGrossCents, 0)

  const employeeNodes = data.employees.map(emp => `
    <Salarie>
      <Matricule>${escapeXml(emp.matriculeCcss)}</Matricule>
      <Nom>${escapeXml(emp.lastName)}</Nom>
      <Prenom>${escapeXml(emp.firstName)}</Prenom>
      <DateNaissance>${emp.birthDate}</DateNaissance>
      <ClasseImpot>${emp.taxClass}</ClasseImpot>
      <DateEntree>${emp.entryDate}</DateEntree>
      <DateSortie>${emp.exitDate ?? ''}</DateSortie>
      <MoisTravailles>${emp.monthsWorked}</MoisTravailles>
      <SalaireBrutAnnuel>${centsToDecimal(emp.annualGrossCents)}</SalaireBrutAnnuel>
      <RevenuImposableAnnuel>${centsToDecimal(emp.annualTaxableIncomeCents)}</RevenuImposableAnnuel>
      <RetenueAnnuelle>${centsToDecimal(emp.annualRTSCents)}</RetenueAnnuelle>
      <CreditsAnnuels>${centsToDecimal(emp.annualCreditsCents)}</CreditsAnnuels>
    </Salarie>`).join('')

  return `<?xml version="1.0" encoding="UTF-8"?>
<ECSP xmlns="urn:lu:acd:ecsp" version="1.0">
  <Employeur>
    <NumeroEmployeur>${escapeXml(data.employerNumber)}</NumeroEmployeur>
    <Denomination>${escapeXml(data.employerName)}</Denomination>
    <NumeroTVA>${escapeXml(data.employerVatNumber)}</NumeroTVA>
  </Employeur>
  <Annee>${data.year}</Annee>
  <NombreSalaries>${data.employees.length}</NombreSalaries>
  <TotalBrut>${centsToDecimal(totalGross)}</TotalBrut>
  <TotalRetenue>${centsToDecimal(totalRTS)}</TotalRetenue>
  <Salaries>${employeeNodes}
  </Salaries>
</ECSP>`
}

// ─── Helpers ─────────────────────────────────────────────────────

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function centsToDecimal(cents: number): string {
  return (cents / 100).toFixed(2)
}

export function rtsXmlFilename(year: number, month: number): string {
  return `RTS_950_${year}${String(month).padStart(2, '0')}.xml`
}

export function ecspXmlFilename(year: number): string {
  return `ECSP_${year}.xml`
}
